import { pdf } from "@react-pdf/renderer";
import { LoadingSpinner } from "components/LoadingSpinner";
import dayjs from "dayjs";
import { Button } from "primereact/button";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useGetMyTrainingQuery } from "store/queries/training";
import { userSelector } from "store/slices/user";
import styled, { createGlobalStyle } from "styled-components";
import { QUIZ_PATH } from "training/quizHelpers";

import { CERTIFICATE_BODY, CertificatePdf } from "./CertificatePdf";

export const CERTIFICATE_PDF_FILE_NAME = "Clear-Certified-certificate.pdf";

/** Only the certificate prints: everything else on the page (the app's bars, menus, buttons) is hidden. */
const PrintStyle = createGlobalStyle`
  @media print {
    body * {
      visibility: hidden !important;
    }

    .clear-certificate,
    .clear-certificate * {
      visibility: visible !important;
    }

    .clear-certificate {
      position: fixed;
      inset: 0;
      margin: 0;
      box-shadow: none;
    }

    @page {
      size: landscape;
      margin: 12mm;
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 24px 0;
`;

const Certificate = styled.section`
  position: relative;
  width: 100%;
  max-width: 860px;
  padding: 48px 40px;
  border: 3px solid var(--brand-purple-700);
  border-radius: 16px;
  background: var(--brand-white, #fff);
  text-align: center;
  box-shadow: 0 8px 24px rgba(79, 70, 229, 0.12);
`;

/**
 * The ribbon. An icon for now; John's ribbon artwork replaces the `<i>` with an SVG inside the same
 * element (`data-ribbon`), so the layout around it does not change.
 */
const Ribbon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: var(--brand-purple-700);
  color: var(--brand-white, #fff);

  .pi-verified {
    font-size: 2.75rem;
  }
`;

const Eyebrow = styled.div`
  color: var(--brand-gray-600);
  font-size: 0.8rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 6px 0 24px;
  color: var(--brand-purple-700);
  font-size: 2.5rem;
  font-weight: 800;
`;

const Certifies = styled.div`
  color: var(--brand-gray-600);
`;

const Name = styled.div`
  margin: 6px 0 8px;
  font-size: 2rem;
  font-weight: var(--font-weight-semibold, 600);
`;

const Hospital = styled.div`
  margin-bottom: 20px;
  font-size: 1.1rem;
`;

const Body = styled.p`
  margin: 0 auto 20px;
  max-width: 460px;
  color: var(--brand-gray-600);
`;

const DateLine = styled.div`
  font-weight: var(--font-weight-semibold, 600);
`;

const Version = styled.div`
  position: absolute;
  right: 16px;
  bottom: 10px;
  color: var(--brand-gray-600);
  font-size: 0.7rem;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--red-600, #dc2626);
`;

/** "October 21, 2026", in the viewer's local time. */
export const formatCertificateDate = (iso: string) =>
  dayjs(iso).format("MMMM D, YYYY");

/**
 * CLE-3015 — /training/certificate. The certified user's certificate: name, hospital, "Clear Certified",
 * the date and (small) the curriculum version, with Print (a print stylesheet that leaves only the
 * certificate), Download PDF (rendered here with @react-pdf/renderer) and Back to Estimates. Anyone not
 * certified is sent to the knowledge check. The route is HospitalStaff-only (WithRoleProtection).
 */
export function CertificatePage() {
  const navigate = useNavigate();
  const { currentAccount, name } = useSelector(userSelector);
  const { data, isLoading, isError } = useGetMyTrainingQuery(currentAccount);
  const { data: accountData } = useGetAccountByIdQuery(currentAccount ?? "", {
    skip: !currentAccount,
  });
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const record = data?.data;
  if (isLoading) return <LoadingSpinner />;
  if (isError || !record?.clearCertifiedAt) {
    return <Navigate to={QUIZ_PATH} replace />;
  }

  const content = {
    name: name ?? "",
    hospitalName: accountData?.data?.name ?? "",
    certifiedDate: formatCertificateDate(record.clearCertifiedAt),
    curriculumVersion: record.clearCertifiedVersion,
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const blob = await pdf(<CertificatePdf {...content} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = CERTIFICATE_PDF_FILE_NAME;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      console.error("[Clear Certified] the certificate PDF failed", error);
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Wrapper>
      <PrintStyle />
      <Certificate
        className="clear-certificate"
        aria-label="Clear Certified certificate"
      >
        <Ribbon data-ribbon>
          <i className="pi pi-verified" aria-hidden />
        </Ribbon>
        <Eyebrow>Certificate of completion</Eyebrow>
        <Title>Clear Certified</Title>
        <Certifies>This certifies that</Certifies>
        <Name data-testid="certificate-name">{content.name}</Name>
        {content.hospitalName ? (
          <Hospital data-testid="certificate-hospital">
            {content.hospitalName}
          </Hospital>
        ) : null}
        <Body>{CERTIFICATE_BODY}</Body>
        <DateLine data-testid="certificate-date">
          {content.certifiedDate}
        </DateLine>
        {content.curriculumVersion ? (
          <Version>Curriculum version {content.curriculumVersion}</Version>
        ) : null}
      </Certificate>
      <Actions>
        <Button
          type="button"
          icon="pi pi-print"
          label="Print"
          className="p-button-outlined"
          onClick={() => window.print()}
        />
        <Button
          type="button"
          icon="pi pi-download"
          label="Download PDF"
          className="p-button-outlined"
          loading={downloading}
          onClick={handleDownload}
        />
        <Button
          type="button"
          label="Back to Estimates"
          onClick={() =>
            navigate(
              currentAccount ? `/hospitals/view/${currentAccount}/orders` : "/"
            )
          }
        />
      </Actions>
      {downloadFailed ? (
        <ErrorText role="alert">
          The PDF could not be created. Use Print and choose &quot;Save as
          PDF&quot; instead.
        </ErrorText>
      ) : null}
    </Wrapper>
  );
}
